import { useCallback, useRef, useState } from 'react'
import { useBreakpoints, useView } from '@10000/react'
import { average } from '@10000/common'
import { TwoColumnView } from '../layout'
import { Body, Box, Button, ElementHighlight, Expertise, useTooltipSeries } from '../widgets'
import { useAnalyticsData } from '.'
import { indexFromValue, meaning, practices, questions } from '../../lookups'
import { PaidContent, PaidContentDialog } from '../free'
import { randomiseLetters } from '../../utilities'
import { useObserver } from '../../hooks'
import './Guidance.scss'

export const Guidance = ({ onClose, displayMode, ...props }) => {
  const { active, practice } = useAnalyticsData()
  const { subpractices = [] } = practices[practice] || {}

  const nextStepGuide = (practice === undefined || isNaN(average(active?.teamAverage?.[practice * 2]))) ? 1 : 0

  return practice === undefined ? null : (
    <TwoColumnView
      startingColumn={nextStepGuide}
      className="guidance"
      onClose={onClose}
      displayMode={displayMode}
      title="Improve your results"
      column1={
        <Subpractice
          practice={practice}
          subpractice={0}
          showNextStepGuide={nextStepGuide === 0}
          value={average(active?.teamAverage?.[practice * 2])}
          displayMode={displayMode}
        />
      }
      column1Label={subpractices[0]?.title}
      column2={
        <Subpractice
          practice={practice}
          subpractice={1}
          showNextStepGuide={nextStepGuide === 1}
          value={average(active?.teamAverage?.[practice * 2 + 1])}
          displayMode={displayMode}
        />
      }
      column2Label={subpractices[1]?.title}
      {...props}
    />
  )
}

const Subpractice = ({ practice, subpractice, value, displayMode, showNextStepGuide }) => {
  const { setNextStepsOpen, setSubpractice } = useAnalyticsData()
  const { title } = practices[practice].subpractices[subpractice]

  const { options } = useView('squiz')
  const index = (indexFromValue(value) === -1 && options?.free) ? 0 : indexFromValue(value)
  const answerText = questions[practice * 2 + subpractice].answers[index]
  const meaningText = meaning[practice * 2 + subpractice][index]
  const { tip } = useTooltipSeries()

  const nextStepsRef = useRef()
  const { mobile } = useBreakpoints()
  const [active, setActive] = useState(false)

  const unset = (isNaN(value) || value === null || value === undefined)

  const nextSteps = () => {
    setNextStepsOpen(true)
    setSubpractice(subpractice)
  }

  const handleNextStepRefInView = useCallback(({ fromInterceptCheck }) => {
    if(tip === 2 && showNextStepGuide && (fromInterceptCheck || !mobile)) {
      setActive(true)
    }
  }, [tip, setActive, mobile, showNextStepGuide])

  const { interceptCheck } = useObserver(nextStepsRef, handleNextStepRefInView)

  return (
    <Box
      container
      column
      gap={32}
      pb={mobile ? 32 : 10}
      px={8}
      fullWidth
      maxWidth={630}
      onTouchEnd={interceptCheck}
    >
      {unset && <>
        <PaidContent />
        <PaidContentDialog />
      </>}

      <Expertise
        title={title?.toUpperCase()}
        value={value}
      />

      <Box column fullWidth gap={8}>
        <Body alt weight={700}>ANSWER</Body>
        <Body alt weight={400} size={24}>{(options?.free && unset) ? randomiseLetters(answerText) : answerText}</Body>
      </Box>

      <Box column fullWidth gap={8}>
        <Body alt weight={700} uppercase>What does it mean?</Body>
        <Body alt weight={400} size={24}>{(options?.free && unset) ? randomiseLetters(meaningText) : meaningText}</Body>
      </Box>

      {!displayMode &&
        <Box>
          <Button onClick={nextSteps} ref={nextStepsRef}>
            Next steps
          </Button>
        </Box>
      }

      {showNextStepGuide && (
        <>
          {mobile && <ElementHighlight
            order={1}
            nextOnFinish
            pointDirection="down"
            manualTarget={{
              left: 0,
              top: window.innerHeight,
              radius: 20,
              width: window.innerWidth,
            }}
          />}
          <ElementHighlight
            order={2}
            target={nextStepsRef}
            nextOnFinish
            pointDirection={mobile ? 'down' : 'left-down'}
            show={active}
          />
        </>
      )}
    </Box>
  )
}